/**
 * IP-based redirect for US visitors
 * Redirects visitors with US IP addresses to us.sophiebillebrahe.com
 *
 * Country lookups use IPinfo Lite (unlimited). If Lite is unavailable or the
 * paid Core quota is exhausted (HTTP 429), the script skips IPinfo for the rest
 * of the month and falls back to ipapi.co, then geojs, then Shopify's
 * browsing_context_suggestions. The same lookup is exposed as
 * window.SBB_IP_REDIRECT.lookupCountry() for the country selector popup.
 */
(function () {
  "use strict";

  /**
   * Visitor IPs that must not be redirected to the US store (office, QA, partners, etc.).
   * Use the exact string returned by ipinfo/ipapi (IPv4 or IPv6).
   */
  const SKIP_REDIRECT_IPS = new Set([
    // Example: "203.0.113.50",
    "24.103.23.146",
  ]);

  const CHECK_COOKIE = "ipRedirectChecked";
  const QUOTA_COOKIE = "ipinfoQuotaExceeded";
  const COUNTRY_POPUP_COOKIE = "country_selector_popup_shown";
  const CHECK_COOKIE_DAYS = 365;
  const IPINFO_TOKEN = "85700463ae7cfd";
  const US_STORE_ORIGIN = "https://us.sophiebillebrahe.com";

  function shouldSkipRedirectForIp(ip) {
    if (!ip || typeof ip !== "string") return false;
    return SKIP_REDIRECT_IPS.has(ip.trim());
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  }

  function setCookie(name, value, days) {
    let cookie = `${name}=${value};path=/;SameSite=Lax`;
    if (days) {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      cookie += `;expires=${expires.toUTCString()}`;
    }
    document.cookie = cookie;
  }

  function daysUntilNextMonth() {
    const now = new Date();
    const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
    return Math.max(1, Math.ceil((nextMonth - now) / (24 * 60 * 60 * 1000)));
  }

  function markIpinfoUnavailable() {
    setCookie(QUOTA_COOKIE, "true", daysUntilNextMonth());
  }

  function shouldUseIpinfo() {
    if (window.SBB_IP_REDIRECT && window.SBB_IP_REDIRECT.useIpinfo === false) {
      return false;
    }
    return getCookie(QUOTA_COOKIE) !== "true";
  }

  function isQuotaExceeded(response, data) {
    if (response.status === 429) return true;
    const payload = JSON.stringify(data || {}).toLowerCase();
    return payload.includes("rate limit") || payload.includes("usage limit") || payload.includes("quota");
  }

  function redirectToUs() {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = US_STORE_ORIGIN + currentPath;
  }

  function handleCountryCheck(countryCode, ip) {
    if (!countryCode) return;

    if (countryCode === "US") {
      if (shouldSkipRedirectForIp(ip)) {
        setCookie(CHECK_COOKIE, "nonUS", CHECK_COOKIE_DAYS);
        return;
      }
      setCookie(CHECK_COOKIE, "US", CHECK_COOKIE_DAYS);
      redirectToUs();
      return;
    }

    setCookie(CHECK_COOKIE, "nonUS", CHECK_COOKIE_DAYS);
  }

  function parseJson(response) {
    return response.json().catch(function () {
      return {};
    });
  }

  function lookupIpinfoLite() {
    return fetch("https://api.ipinfo.io/lite/me?token=" + IPINFO_TOKEN)
      .then(function (response) {
        return parseJson(response).then(function (data) {
          if (isQuotaExceeded(response, data)) {
            markIpinfoUnavailable();
            return null;
          }
          if (!response.ok) return null;
          if (!data.country_code) return null;
          return { country: data.country_code, ip: data.ip };
        });
      })
      .catch(function () {
        return null;
      });
  }

  function lookupIpapi() {
    return fetch("https://ipapi.co/json/")
      .then(function (response) {
        if (!response.ok) throw new Error("ipapi.co failed");
        return response.json();
      })
      .then(function (data) {
        if (!data.country_code) return null;
        return { country: data.country_code, ip: data.ip };
      })
      .catch(function () {
        return null;
      });
  }

  function lookupGeojs() {
    return fetch("https://get.geojs.io/v1/ip/country.json")
      .then(function (response) {
        if (!response.ok) throw new Error("geojs failed");
        return response.json();
      })
      .then(function (data) {
        if (!data.country) return null;
        return { country: data.country, ip: data.ip };
      })
      .catch(function () {
        return null;
      });
  }

  function lookupShopifyDetectedCountry() {
    return fetch("/browsing_context_suggestions.json")
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (data) {
        const country = data && data.detected_values && data.detected_values.country;
        if (!country) return null;
        const code = country.iso_code || country.handle;
        if (!code || typeof code !== "string" || code.length !== 2) return null;
        return { country: code.toUpperCase(), ip: null };
      })
      .catch(function () {
        return null;
      });
  }

  let countryLookupPromise = null;

  function lookupVisitorCountry() {
    if (countryLookupPromise) return countryLookupPromise;

    const ipinfoLookup = shouldUseIpinfo() ? lookupIpinfoLite() : Promise.resolve(null);

    countryLookupPromise = ipinfoLookup
      .then(function (result) {
        if (result) return result;
        return lookupIpapi();
      })
      .then(function (result) {
        if (result) return result;
        return lookupGeojs();
      })
      .then(function (result) {
        if (result) return result;
        return lookupShopifyDetectedCountry();
      })
      .catch(function () {
        return null;
      });

    return countryLookupPromise;
  }

  window.SBB_IP_REDIRECT = window.SBB_IP_REDIRECT || {};
  window.SBB_IP_REDIRECT.lookupCountry = lookupVisitorCountry;

  if (window.location.hostname === "us.sophiebillebrahe.com") {
    return;
  }

  // Visitor already confirmed or dismissed the country popup — keep that choice.
  if (getCookie(COUNTRY_POPUP_COOKIE) === "true") {
    return;
  }

  const checked = getCookie(CHECK_COOKIE);
  if (checked === "nonUS") {
    return;
  }
  if (checked === "US") {
    redirectToUs();
    return;
  }

  lookupVisitorCountry()
    .then(function (result) {
      if (result) handleCountryCheck(result.country, result.ip);
    })
    .catch(function () {
      // Don't block page load if every lookup fails.
    });
})();
